import { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { Avatar, Checkbox, Tooltip, TextField, MenuItem, IconButton, InputAdornment } from '@mui/material';
import BaseNewAndDetail from '../utility/outlines/BaseNewAndDetail';
import {
  cropClasses,
  errFeedbackFn,
  externalDetailClasses,
  fadeTimingMs,
  getHandleErrorWithFeedbackCX,
  metrcVersion,
  miscClasses,
  NEW,
  popupClasses,
} from '../utility/constants';
import useThemeVal from '../../utility/useThemeVal';
import DisplayTable from '../utility/DisplayTable';
import EntityDetailPopup from '../utility/EntityDetailPopup';
import NoEntityMsg from '../utility/messages/NoEntityMsg';
import APIService from '../../api/apiService';
import { DetailAndDelBtn, DownArrowIcon } from '../utility';
import { endpoints } from '../utility/constants';
import { FeedbackContext } from '../../App';
import { IntegrationsBreadcrumbs } from '../utility/BreadcrumbLinks';
import MetrcCards from './utils/MetrcCards';
import DeleteAlertBox from '../utility/messages/DeleteAlertBox';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const integrationContent = {
  1: {
    name: 'Metrc',
    about:
      'Metrc is the most trusted and experienced provider of cannabis regulatory systems in the United States. Our solution combines advanced software, radio-frequency identification (RFID) technology, a dedicated customer-support team, and a secure database to track and trace cannabis from growth, harvest, and processing to testing, transport, and sale',
    link: 'https://www.metrc.com/',

    tabEntityMap: [
      { key: 'overview', label: 'Overview', defaultForDetail: true },
      { key: 'metrickey', label: 'Metrc Key' },
      { key: 'data', label: 'Metrc Data' },
    ],
  },
  2: {
    name: 'TrolMaster',
    about:
      'TrolMaster Agro Instruments Co., Ltd. is a leading agriculture technology company specializing in advanced environmental controllers for the horticultural industry.',
    link: 'https://www.trolmaster.com/',
    tabEntityMap: [
      { key: 'overview', label: 'Overview', defaultForDetail: true },
      { key: 'guidelines', label: 'Guidelines' },
      { key: 'privacy-policy', label: 'Privacy Policy' },
      { key: 'api', label: 'APIs Key ' },
    ],
  },
};

const tableTitles = [
  { key: 'default', title: 'Default', minWidth: 40 },
  { key: 'metrc_id', title: 'Metrc Key', minWidth: 100 },
  { key: 'state', title: 'State' },
  { key: 'environments', title: 'Environment' },
  { key: 'version', title: 'Metrc Version' },
  { key: 'metrc_status', title: 'Status', minWidth: 40 },
  { key: 'actions', title: 'Actions' },
];

const apitableTitles = [
  { key: 'serial_number', title: 'S.No', minWidth: 40 },
  { key: 'farm_name', title: 'Farm Name', minWidth: 100 },
  { key: 'x_api_key', title: 'X Api Key', minWidth: 150 },
  { key: 'version', title: 'Version', minWidth: 40 },
  { key: 'models', title: 'Senser Models', minWidth: 40 },
  { key: 'metrc_status', title: 'Status', minWidth: 40 },
  { key: 'actions', title: 'Actions' },
];

const baseSaveBtnChildren = 'Add X API Key';

const getMetrcTempRef = metrc_ids => {
  let num;
  const result = metrc_ids?.length
    ? metrc_ids?.reduce((res, val) => {
        if (val?.tempRef) {
          num = Number(val?.tempRef?.split('-')?.[1]);
          if (num > res) return num;
        }
        return res;
      }, 0)
    : 0;
  return `metrc-${result}`;
};

const getApiTempRef = api_keys => {
  let num;
  const result = api_keys?.length
    ? api_keys?.reduce((res, val) => {
        if (val?.tempRef) {
          num = Number(val?.tempRef?.split('-')?.[1]);
          if (num > res) return num;
        }
        return res;
      }, 0)
    : 0;
  return `api-${result + 1}`;
};

export default function ExternalDetail(props) {
  const { title = '' } = props;
  const feedbackCX = useContext(FeedbackContext);
  const [savedRes, setSavedRes] = useState({});
  const [values, setValues] = useState({});
  const { integrationid } = useParams();
  const currDarkThemeClass = useThemeVal();
  const [farmList, setFarmList] = useState([]);
  const [trolmasterModels, setTrolmasterModels] = useState([]);
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteTargetEntry, setDeleteTargetEntry] = useState(null);
  const currentIntegration = integrationContent[integrationid] || integrationContent[1];
  const [showApiKey, setShowApiKey] = useState(false);

  const handleSetValues = (prop, value) => setValues(state => ({ ...state, [prop]: value }));
  const handleSetSavedRes = (prop, value) => setSavedRes(state => ({ ...state, [prop]: value }));
  const handleSetChangeTab = value => handleSetValues('changeTab', value);

  useEffect(() => {
    handleSetValues('name', currentIntegration.name);
    handleSetSavedRes('name', currentIntegration.name);
    if (integrationid === '1') {
      fetchMetrcCollectionEntries();
    }
    else if (integrationid === '2'){
      fetchTrolMasterApiKeys();
    }
  }, [integrationid]);

  const fetchMetrcCollectionEntries = async () => {
    try {
      const response = await APIService.fetchInstance('collection/metrc/');
      setValues(state => ({ ...state, ...response.data }));
      setSavedRes(state => ({ ...state, ...response.data }));
    } catch (error) {
      getHandleErrorWithFeedbackCX(feedbackCX)(errFeedbackFn({}));
    }
  };

  const fetchTrolMasterApiKeys = async () => {
    try {
      const response = await APIService.fetchInstance('integration-trolmaster/');
      const apiKeysArray = Array.isArray(response.data) ? response.data : [];
      setValues(state => ({ ...state, api_keys: apiKeysArray }));
      setSavedRes(state => ({ ...state, api_keys: apiKeysArray }));
      const modelRes = await APIService.fetchInstance("new/sensormodel/");
      setTrolmasterModels( Array.isArray(modelRes?.data?.sensormodel)? modelRes.data.sensormodel: []);
      const farmRes = await APIService.fetchInstance("farm/?fetch_farms=true");
      setFarmList(farmRes.data?.farms || farmRes.data || []);
    } catch (error) {
      console.error('Fetch API keys error:', error);
      getHandleErrorWithFeedbackCX(feedbackCX)(errFeedbackFn({}));
    }
  };

  const handleEditMetrc = entry => {
    handleSetValues('metrcOpen', true);
    handleSetValues('selMetrc', entry);
    handleSetValues('selectedMetrc', entry?.id ?? entry?.tempRef);
  };

  const handleAddMetrc = () => {
    handleSetValues('metrcOpen', true);
    let tempRef = getMetrcTempRef(values?.metrc_ids ?? []);
    handleSetValues('selMetrc', {
      metrc_id: '',
      state: '',
      environment_type: '',
      version: 'v2',
      metrc_status: 0,
      default: false,
      tempRef,
    });
    handleSetValues('selectedMetrc', tempRef);
  };

  const handleDeleteMetrc = async id => {
    try {
      const entityWithID = endpoints.metrc.detail(id);
      await APIService.deleteInstance(entityWithID);
      const updatedEntries = values?.metrc_ids.filter(entry => entry.id !== id);
      handleSetValues('metrc_ids', updatedEntries);
    } catch (error) {
      getHandleErrorWithFeedbackCX(feedbackCX)(errFeedbackFn({}));
    } finally {
      handleFinally();
    }
  };

  const handleModifyMetrcRes = res => {
    handleSetValues(
      'metrc_ids',
      values?.metrc_ids?.map(e => (e?.id && e?.id === values?.selectedMetrc ? res.data : e))
    );
  };

  const handleMetrcAddRes = res => {
    handleSetValues('metrc_ids', [...values?.metrc_ids, res.data]);
  };

  const handleFinally = () => {
    handleSetValues('loading', false);
    handlePopupClose();
  };

  const handleAddOrEditMetrc = () => {
    handleSetValues('loading', true);
    let isEdit = !isNaN(+values?.selectedMetrc);
    if (isEdit) {
      let entityWithID = endpoints.metrc.detail(+values?.selectedMetrc);
      APIService.modifyInstance(entityWithID, values?.selMetrc)
        .then(handleModifyMetrcRes)
        .catch(getHandleErrorWithFeedbackCX(feedbackCX)(errFeedbackFn({})))
        .finally(handleFinally);
    } else {
      let entity = endpoints.metrc.list;
      APIService.createInstance(entity, values?.selMetrc)
        .then(handleMetrcAddRes)
        .catch(getHandleErrorWithFeedbackCX(feedbackCX)(errFeedbackFn({})))
        .finally(handleFinally);
    }
  };

  const handleCheckboxChange = async id => {
    try {
      const removedEntry = values?.metrc_ids.find(entry => entry.default);
      const removed_id = removedEntry?.id;
      const newDefaultEntry = values?.metrc_ids.find(entry => entry.id === id);

      const payload = {
        ...newDefaultEntry,
        default: true,
        default_id: id,
        removed_id: removed_id,
        metrc_status: newDefaultEntry.metrc_status === 'Active',
      };

      await APIService.modifyInstance(`metrc-key/${id}/`, payload);

      setValues(prevValues => ({
        ...prevValues,
        metrc_ids: prevValues.metrc_ids.map(entry => (entry.id === id ? { ...entry, default: true } : { ...entry, default: false })),
      }));
    } catch (error) {
      console.error('Error setting default Metrc entry:', error);
    }
  };

  // TrolMaster API Key handlers
  const handleEditApiKey = entry => {
    handleSetValues('apiKeyOpen', true);
    const editData = {
      ...entry,
      farm: entry.farm || '',
      x_api_key: entry.x_api_key || '',
      version: entry.version ? entry.version.toLowerCase() : 'V2',
      trolmaster: Array.isArray(entry.trolmaster) ? entry.trolmaster.map(Number) : [],
    };
    handleSetValues('selApiKey', editData);
    handleSetValues('selectedApiKey', entry?.farm ?? entry?.tempRef);
  };

  const handleAddApiKey = () => {
    handleSetValues('apiKeyOpen', true);
    let tempRef = getApiTempRef(values?.api_keys ?? []);
    handleSetValues('selApiKey', {
      farm: '',
      x_api_key: '',
      version: 'v2',
      trolmaster: [],
      api_status: false,
      default: false,
      tempRef,
    });
    handleSetValues('selectedApiKey', tempRef);
  };

  const handleDeleteApiKey = async entry => {
    try {
      const farmId = entry.farm || entry.farm_id;

      if (!farmId) {
        throw new Error('Farm ID is required for deletion');
      }

      await APIService.deleteInstance(`integration-trolmaster/${farmId}/`);

      const updatedEntries = values?.api_keys.filter(e => (e.farm || e.farm_id) !== farmId);
      handleSetValues('api_keys', updatedEntries);

      feedbackCX.setFeedback({
        open: true,
        message: 'API Key deleted successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Delete API key error:', error);
      getHandleErrorWithFeedbackCX(feedbackCX)(
        errFeedbackFn({
          message: error?.message || 'Failed to delete API Key',
        })
      );
    } finally {
      setDeletePopup(false);
      setDeleteTargetEntry(null);
      handleSetValues('loading', false);
    }
  };

  const handleDeleteClick = entry => {
    setDeleteTargetEntry(entry);
    setDeletePopup(true);
  };

  const handleConfirmDelete = () => {
    if (deleteTargetEntry) {
      handleSetValues('loading', true);
      handleDeleteApiKey(deleteTargetEntry);
    }
  };

  const handleModifyApiKeyRes = res => {
    handleSetValues(
      'api_keys',
      values?.api_keys?.map(e => (e?.farm === values?.selApiKey?.farm ? res.data : e))
    );
  };

  const handleApiKeyAddRes = res => {
    handleSetValues('api_keys', [...values?.api_keys, res.data]);
  };

  const handleAddOrEditApiKey = async () => {
    handleSetValues('loading', true);

    const farmId = values?.selApiKey?.farm;

    if (!farmId) {
      feedbackCX.setFeedback({
        open: true,
        message: 'Farm is required',
        severity: 'error',
      });
      handleSetValues('loading', false);
      return;
    }

    const payload = {
      farm: farmId,
      x_api_key: values?.selApiKey?.x_api_key,
      version: values?.selApiKey?.version || 'V2',
      trolmaster: values?.selApiKey?.trolmaster || [],
    };

    try {
      let res;
      // EDIT - Use farm_id in the URL
      if (!isNaN(+values.selectedApiKey)) {
        res = await APIService.modifyInstance(`integration-trolmaster/${farmId}/`, payload);
        handleModifyApiKeyRes(res);

        feedbackCX.setFeedback({
          open: true,
          message: 'API Key updated successfully',
          severity: 'success',
        });
      }
      // CREATE
      else {
        res = await APIService.createInstance(`integration-trolmaster/`, payload);
        handleApiKeyAddRes(res);

        feedbackCX.setFeedback({
          open: true,
          message: 'API Key created successfully',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('API Key save error:', error);
      getHandleErrorWithFeedbackCX(feedbackCX)(
        errFeedbackFn({
          message: error?.response?.data?.message || 'Failed to save API Key',
        })
      );
    } finally {
      handleFinally();
    }
  };

  const handlePopupClose = () => {
    handleSetValues('metrcOpen', false);
    handleSetValues('apiKeyOpen', false);
    setShowApiKey(false);
  };

  const getStatusColor = status => {
    if (status === false) {
      return '#f44336';
    } else if (status === true) {
      return '#4caf50';
    } else {
      return '#000';
    }
  };

  const getEnvironmentName = code => {
    const environment = savedRes?.environments?.find(env => env.code === code);
    return environment ? environment.name : 'Unknown Environment';
  };

  const tableEntries = (values?.metrc_ids ?? [])?.map((entry, i) => [
    <Checkbox checked={entry.default} onChange={() => handleCheckboxChange(entry.id)} />,
    entry?.metrc_id,
    entry?.state,
    getEnvironmentName(entry?.environment_type),
    entry?.version,
    <Tooltip title={entry.metrc_status ? 'Inactive' : 'Active'} arrow>
      <Avatar sx={{ backgroundColor: getStatusColor(entry?.metrc_status), width: 24, height: 24 }}>{entry?.metrc_status}</Avatar>
    </Tooltip>,
    <DetailAndDelBtn
      toShowDelBtn={false}
      onDetailClick={() => handleEditMetrc(entry)}
      onDelClick={() => handleDeleteMetrc(entry?.id ?? entry?.tempRef)}
    />,
  ]);

  const getFarmNameWithLicense = farmId => {
    if (!farmId || !farmList?.length) return '—';

    const farm = farmList.find(f => Number(f.id || f.farm_id || f.farm) === Number(farmId));

    if (!farm) return '—';

    return farm.license_number || farm.farm_name || farm.name || '—';
  };

  const getSensorModelNames = (ids = []) => {
    if (!Array.isArray(ids) || ids.length === 0) return '—';

    const names = ids.map(id => trolmasterModels.find(m => m.id === id)?.model).filter(Boolean);

    return names.length ? names.join(', ') : '—';
  };

  const apiKeyTableEntries = (values?.api_keys ?? []).map((entry, index) => [
    index + 1,
    getFarmNameWithLicense(entry.farm),
    entry?.x_api_key ? '••••••••' + entry.x_api_key.slice(-4) : '—',
    entry?.version || 'V2',
    getSensorModelNames(entry?.trolmaster),
    <Tooltip title={entry.is_active ? 'Active' : 'Inactive'} arrow>
      <Box
        sx={{
          backgroundColor: getStatusColor(entry.is_active),
          width: 24,
          height: 24,
          margin: 'auto',
          borderRadius: '50%',
        }}
      />
    </Tooltip>,
    <DetailAndDelBtn toShowDelBtn={true} onDetailClick={() => handleEditApiKey(entry)} onDelClick={() => handleDeleteClick(entry)} />,
  ]);

  const OverviewTab = (
    <Fade in={true} timeout={fadeTimingMs}>
      <Box className={`${externalDetailClasses?.box} ${currDarkThemeClass}`}>
        <Typography className={`${externalDetailClasses?.tabTitleTypo} ${currDarkThemeClass}`}>About</Typography>
        <Typography className={`${externalDetailClasses?.tabContentTypo} ${currDarkThemeClass}`}>{currentIntegration.about}</Typography>
        <Typography className={`${externalDetailClasses?.tabTitleTypo} ${currDarkThemeClass}`}>Links</Typography>
        <Link className={`${externalDetailClasses?.tabContentLinkTypo} ${currDarkThemeClass}`} href={currentIntegration.link} target="_blank">
          {currentIntegration.link}
        </Link>
      </Box>
    </Fade>
  );
  const listData = [
    {
      name: 'Items',
      description: 'It Consist of Items, Category Data.',
    },
    {
      name: 'Waste',
      description: 'It Consist of Waste Reason, Waste Method',
    },
    {
      name: 'Unit',
      description: ' It Consist of Units',
    },
    {
      name: 'Lab Tests',
      description: 'It consists of lab tests',
    },
    {
      name: 'Metrc Tags',
      description: 'It consists of Plant tags, Package tags',
    },
    {
      name: 'Facility Employees',
      description: 'It consists of Metrc Facility Employees.',
    },
    {
      name: 'Metrc Transporters',
      description: 'It consists of Metrc Transporters, and Vehicles.',
    },
  ];

  const MetrcDataTab = (
    <Fade in={true} timeout={fadeTimingMs}>
      <Box className={`${externalDetailClasses?.box} ${currDarkThemeClass}`} sx={{ marginTop: 3 }}>
        <MetrcCards listData={listData} integrationid={integrationid} currDarkThemeClass={currDarkThemeClass} />
      </Box>
    </Fade>
  );

  const MetrcKeyTab = (
    <Fade in={true} timeout={fadeTimingMs}>
      <Box className={`${externalDetailClasses?.box} ${currDarkThemeClass}`}>
        <Typography className={`${externalDetailClasses?.tabTitleTypo} ${currDarkThemeClass}`} sx={{ mt: '20px', pb: '20px' }}>
          Metrc Key
        </Typography>
        <DisplayTable
          entity="Metrc Entry"
          toShowAddBtn={false}
          tableTitles={tableTitles}
          tableEntries={tableEntries}
          currDarkThemeClass={currDarkThemeClass}
          onClick={handleAddMetrc}
        />
      </Box>
    </Fade>
  );
  const ApiKeyTab = (
    <Fade in={true} timeout={fadeTimingMs}>
      <Box className={`${externalDetailClasses?.box} ${currDarkThemeClass}`}>
        <Typography className={`${externalDetailClasses?.tabTitleTypo} ${currDarkThemeClass}`} sx={{ mt: '20px', pb: '20px' }}>
          TrolMaster API Keys
        </Typography>
        <DisplayTable
          entity="API Key"
          toShowAddBtn={false}
          tableTitles={apitableTitles}
          tableEntries={apiKeyTableEntries}
          currDarkThemeClass={currDarkThemeClass}
          onClick={handleAddApiKey}
        />
      </Box>
    </Fade>
  );

  const GuidelinesTab = (
    <Fade in={true} timeout={fadeTimingMs}>
      <Box className={`${externalDetailClasses?.box} ${currDarkThemeClass}`}>
        <Typography className={`${externalDetailClasses?.tabTitleTypo} ${currDarkThemeClass}`}>Guidelines will be available soon</Typography>
      </Box>
    </Fade>
  );

  const PrivacyPolicyTab = (
    <Fade in={true} timeout={fadeTimingMs}>
      <Box className={`${externalDetailClasses?.box} ${currDarkThemeClass}`}>
        <Typography className={`${externalDetailClasses?.tabTitleTypo} ${currDarkThemeClass}`}>Privacy Policy will be available soon</Typography>
      </Box>
    </Fade>
  );

  const EndpointsTab = (
    <Fade in={true} timeout={fadeTimingMs}>
      <Box className={`${externalDetailClasses?.box} ${currDarkThemeClass}`}>
        <NoEntityMsg msg="Application is under development" isCreateEnabled={false} entity="Endpoints/API" />
      </Box>
    </Fade>
  );

  const defUndVal = '';

  const baseEndpointCheck = integrationid === NEW;
  const navigate = useNavigate();
  const breadcrumbsProps = {
    currLink: baseEndpointCheck ? values?.name ?? 'Create Integration' : savedRes?.name ?? defUndVal,
    currDarkThemeClass,
    navigate,
  };
  const breadcrumbs = <IntegrationsBreadcrumbs {...breadcrumbsProps} />;
  const tabContentMap = {
    overview: OverviewTab,
    guidelines: GuidelinesTab,
    'privacy-policy': PrivacyPolicyTab,
    ...(integrationid === '1' && {
      metrickey: MetrcKeyTab,
      data: MetrcDataTab,
      endpoints: EndpointsTab,
    }),
    ...(integrationid === '2' && {
      api: ApiKeyTab,
    }),
  };

  const showSaveBtn = tab => {
    return integrationid === '2' && tab === 'api';
  };
  const handleSave = () => {
    handleAddApiKey();
  };

  const externalDetailProps = {
    baseEntityName: savedRes?.name,
    baseid: integrationid,
    currDarkThemeClass,
    tabContentMap,
    tabEntityMap: currentIntegration.tabEntityMap,
    breadcrumbs,
    createBaseEntityMsg: '',
    baseSaveBtnChildren,
    baseEndpointCheck: false,
    enablePopup: true,
    showSaveBtn,
    handleSave,
    changeTab: values?.changeTab,
    handleSetChangeTab,
  };

  const metrcPopupProps = {
    loading: values?.loading,
    open: values?.metrcOpen,
    currDarkThemeClass,
    onClose: handlePopupClose,
    modalMaxWidth: '860px',
    toDisableSave: false,
    toShowSave: true,
    headContentText: 'Edit or Add Metrc Entry',
    saveText: 'Save',
    title: values?.metrcEdit ? 'Edit Metrc' : 'Add Metrc',
    onSaveClick: () => handleAddOrEditMetrc(),
  };

  const apiKeyPopupProps = {
    loading: values?.loading,
    open: values?.apiKeyOpen,
    currDarkThemeClass,
    onClose: handlePopupClose,
    modalMaxWidth: '860px',
    toDisableSave: false,
    toShowSave: true,
    headContentText: '',
    saveText: 'Save',
    title: values?.selectedApiKey && !isNaN(+values?.selectedApiKey) ? 'Edit X API Key' : 'Add X API Key',
    onSaveClick: () => handleAddOrEditApiKey(),
  };

  const deleteAlertBoxProps = {
    open: deletePopup,
    onClose: () => {
      setDeletePopup(false);
      setDeleteTargetEntry(null);
    },
    onDelete: handleConfirmDelete,
    currDarkThemeClass,
    deleteBtn: 'Delete',
    cancelBtn: 'Cancel',
    title: `Are you sure you want to delete this X API Key?`,
    headContentText: deleteTargetEntry
      ? `Farm: ${getFarmNameWithLicense(deleteTargetEntry.farm)}. This action cannot be undone.`
      : 'This action cannot be undone.',
  };

  const stateMenuItemProps = {
    className: `${cropClasses?.menuItem} ${currDarkThemeClass}`,
    focusVisibleClassName: miscClasses?.menuItemFocusDark,
  };

  const metrcVersionList = metrcVersion?.map((e, i) => (
    <MenuItem key={e.id} value={e.name} {...stateMenuItemProps}>
      {e.name}
    </MenuItem>
  ));

  if (!metrcVersionList?.length) {
    metrcVersionList?.push(
      <MenuItem key="v2" value="v2" disabled {...stateMenuItemProps}>
        v2
      </MenuItem>
    );
  }

  const list =
    savedRes?.states?.map((e, i) => (
      <MenuItem key={i} value={e?.code} {...stateMenuItemProps}>
        {e?.name}
      </MenuItem>
    )) ?? [];

  if (!list?.length) {
    list?.push(
      <MenuItem key={-1} value={-1} disabled {...stateMenuItemProps}>
        No Available States.
      </MenuItem>
    );
  }

  const environmentList =
    savedRes?.environments?.map((e, i) => (
      <MenuItem key={i} value={e?.code} {...stateMenuItemProps}>
        {e?.name}
      </MenuItem>
    )) ?? [];

  if (!environmentList?.length) {
    environmentList?.push(
      <MenuItem key={-1} value={-1} disabled {...stateMenuItemProps}>
        No Available Environments.
      </MenuItem>
    );
  }

  const textFieldProp = ({ prop, inputProps = {}, otherTextProps = {} }) => ({
    autoComplete: 'off',
    className: `${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`,
    fullWidth: true,
    inputProps,
    label: '',
    onChange: ({ target: { value } }) => handleSetValues('selMetrc', { ...values?.selMetrc, [prop]: value }),
    required: true,
    value: values?.selMetrc?.[prop],
    ...otherTextProps,
  });

  const apiKeyTextFieldProp = ({ prop, inputProps = {}, otherTextProps = {} }) => {
    const currentValue = values?.selApiKey?.[prop];
    return {
      autoComplete: 'off',
      className: `${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`,
      fullWidth: true,
      inputProps,
      label: '',
      onChange: ({ target: { value } }) => {
        let newValue = value;

        if (prop === 'trolmaster') {
          newValue = Array.isArray(value) ? value : [value];
        }
        handleSetValues('selApiKey', {
          ...values.selApiKey,
          [prop]: newValue,
        });
      },
      required: true,
      value: currentValue !== undefined && currentValue !== null ? currentValue : prop === 'trolmaster' ? [] : '',
      ...otherTextProps,
    };
  };

  const metrcKeyProps = {
    prop: 'metrc_id',
    otherTextProps: {
      label: 'Metrc Key',
    },
  };

  const stateProps = {
    prop: 'state',
    otherTextProps: {
      label: 'State',
      required: true,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const environmentProps = {
    prop: 'environment_type',
    otherTextProps: {
      label: 'Environment',
      required: true,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const metrcVersionProps = {
    prop: 'version',
    otherTextProps: {
      label: 'Metrc Version',
      required: true,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const xApiKeyProps = {
    prop: 'x_api_key',
    otherTextProps: {
      label: 'X-API Key',
      required: true,
      type: showApiKey ? 'text' : 'password',
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowApiKey(!showApiKey)}
              onMouseDown={e => e.preventDefault()}
              edge="end"
            >
              {showApiKey ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      },
    },
  };

  const farmProps = {
    prop: 'farm',
    otherTextProps: {
      label: 'Farm',
      required: true,
      select: true,
      disabled: !isNaN(+values?.selectedApiKey),
      SelectProps: {
        IconComponent: DownArrowIcon,
        renderValue: selected => {
          if (!selected) return '';
          const farm = farmList.find(f => {
            const farmId = f.id || f.farm_id;
            const match = Number(farmId) === Number(selected);
            return match;
          });
          if (!farm) return `ID: ${selected}`;
          return farm?.license_number || farm?.farm_name || farm?.name || '—';
        },
      },
    },
  };

  const versionProps = {
    prop: 'version',
    otherTextProps: {
      label: 'Version',
      required: true,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const trolmasterProps = {
    prop: 'trolmaster',
    otherTextProps: {
      label: 'Sensor Models',
      required: true,
      select: true,
      SelectProps: {
        multiple: true,
        IconComponent: DownArrowIcon,
        renderValue: selected =>
          trolmasterModels
            ?.filter(model => selected.includes(model.id))
            ?.map(model => model.model)
            ?.join(', '),
      },
    },
  };

  return (
    <>
      <BaseNewAndDetail {...externalDetailProps} />
      {values?.metrcOpen && integrationid === '1' && (
        <EntityDetailPopup {...metrcPopupProps}>
          <TextField {...textFieldProp(metrcKeyProps)} />
          <TextField {...textFieldProp(stateProps)}>{list}</TextField>
          <TextField {...textFieldProp(metrcVersionProps)}>{metrcVersionList}</TextField>
          <TextField {...textFieldProp(environmentProps)}>{environmentList}</TextField>
        </EntityDetailPopup>
      )}
      {values?.apiKeyOpen && integrationid === '2' && (
        <EntityDetailPopup {...apiKeyPopupProps}>
          <TextField {...apiKeyTextFieldProp(farmProps)}>
            {farmList?.length > 0 ? (
              farmList.map(farm => {
                const farmId = farm.farm_id || farm.id;
                const displayText = farm.license_number || farm.farm_name || farm.name;
                return (
                  <MenuItem key={farmId} value={farmId} {...stateMenuItemProps}>
                    {displayText}
                  </MenuItem>
                );
              })
            ) : (
              <MenuItem value="" disabled {...stateMenuItemProps}>
                No farms available
              </MenuItem>
            )}
          </TextField>

          <TextField {...apiKeyTextFieldProp(xApiKeyProps)} />

          <TextField {...apiKeyTextFieldProp(versionProps)}>
            {/* <MenuItem value="v1" {...stateMenuItemProps}>v1</MenuItem> */}
            <MenuItem value="v2" {...stateMenuItemProps}>
              v2
            </MenuItem>
          </TextField>

          <TextField {...apiKeyTextFieldProp(trolmasterProps)}>
            {trolmasterModels?.map(model => (
              <MenuItem key={model.id} value={model.id} {...stateMenuItemProps}>
                <Checkbox checked={(values?.selApiKey?.trolmaster || []).includes(model.id)} />
                {model.model}
              </MenuItem>
            ))}
          </TextField>
        </EntityDetailPopup>
      )}
      {deletePopup && <DeleteAlertBox {...deleteAlertBoxProps} />}
    </>
  );
}
